import React from 'react';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import RoomBookingUserAvatar from '../RoomBookingUserAvatar';
import TutorBookingScheduleBlock from '../tutor/TutorBookingScheduleBlock';
import UserBandAffiliationLine from '../UserBandAffiliationLine';
import { CalendarEvent, CalendarEventSource, CalendarEventStatus } from '../../models/CalendarEvent';
import { formatTutorBookingScheduleLabels } from '../../utils/tutorBookingChangeProposal';
import { roomBookingDetailsPath } from '../../utils/roomBookingDetailsPath';
import { useThemeContext } from '../../contexts/ThemeContext';

export type RoomCalendarEventDetails = CalendarEvent & {
  roomId?: string;
  roomName?: string;
  isManualBlock?: boolean;
};

export interface RoomCalendarEventDetailsDialogProps {
  open: boolean;
  event: RoomCalendarEventDetails | null;
  onClose: () => void;
  canDeleteBlock?: boolean;
  deletingBlock?: boolean;
  onRequestDeleteBlock?: () => void;
}

const BLOCK_ACCENT = '#FFB648';
const EXTERNAL_ACCENT = '#6B7B8C';
const BOOKING_ACCENT = '#1976d2';

function formatDurationMinutes(minutes: number, minLabel: string, hrLabel: string, hrsLabel: string): string {
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (hours === 0) return `${remaining} ${minLabel}`;
  if (remaining === 0) return `${hours} ${hours > 1 ? hrsLabel : hrLabel}`;
  return `${hours} ${hours > 1 ? hrsLabel : hrLabel} ${remaining} ${minLabel}`;
}

function resolveDisplayTitle(event: RoomCalendarEventDetails): string {
  const raw = event.title?.trim() || '';
  if (!raw) return '';
  if (raw.includes(':')) {
    const [, ...rest] = raw.split(':');
    const description = rest.join(':').trim();
    if (description) return description;
  }
  return raw;
}

function statusChipColor(
  status: CalendarEventStatus | string | undefined,
): 'default' | 'success' | 'warning' | 'error' {
  const normalized = String(status ?? '').toUpperCase();
  if (normalized === CalendarEventStatus.CONFIRMED) return 'success';
  if (normalized === CalendarEventStatus.AWAITING_CONFIRMATION) return 'warning';
  if (normalized === CalendarEventStatus.CANCELLED) return 'error';
  return 'default';
}

function statusLabel(
  t: (key: string, fallback: string) => string,
  status: CalendarEventStatus | string | undefined,
): string {
  const normalized = String(status ?? '').toLowerCase();
  if (!normalized) return t('status.unknown', 'Unknown');
  return t(`status.${normalized}`, status ? String(status) : 'Unknown');
}

export default function RoomCalendarEventDetailsDialog({
  open,
  event,
  onClose,
  canDeleteBlock = false,
  deletingBlock = false,
  onRequestDeleteBlock,
}: RoomCalendarEventDetailsDialogProps) {
  const { t, i18n } = useTranslation();
  const { mode: themeMode } = useThemeContext();

  const source = String(event?.source ?? '').toUpperCase();
  const isManualBlock = Boolean(event?.isManualBlock);
  const isExternal = source === CalendarEventSource.GOOGLE_CALENDAR_SYNCED_BOOKING;
  const isBlock = isManualBlock || isExternal;
  const calendarEventId = event?.id?.includes('_merged_')
    ? event.id.split('_merged_')[0]
    : event?.id?.trim() || null;

  const displayTitle =
    (event ? resolveDisplayTitle(event) : '') ||
    (isBlock
      ? t('spaceCalendar.block', 'Block')
      : t('spaceCalendar.booking', 'Booking'));

  const bookerName = event?.userName?.trim() || null;
  const bookerPhone = event?.phoneNumber?.trim() || null;
  const bookerBand = event?.bandName?.trim() || null;
  const bookingHeaderName = bookerName || displayTitle;

  const roomName = event?.roomName?.trim() || null;

  const schedule = event
    ? formatTutorBookingScheduleLabels(
        event.startTime.endsWith('Z') ? event.startTime : `${event.startTime}Z`,
        event.endTime.endsWith('Z') ? event.endTime : `${event.endTime}Z`,
        i18n.language,
        (minutes) =>
          formatDurationMinutes(
            minutes,
            t('roomBookingRequests.min', 'min'),
            t('roomBookingRequests.hr', 'hr'),
            t('roomBookingRequests.hrs', 'hrs'),
          ),
      )
    : null;

  const accentColor = isManualBlock
    ? BLOCK_ACCENT
    : isExternal
      ? EXTERNAL_ACCENT
      : BOOKING_ACCENT;

  const bookingId = event?.roomBookingId?.trim() || null;
  const bookingDetailsTo = bookingId ? roomBookingDetailsPath('host', bookingId) : null;

  const statusChip = isBlock ? (
    <Chip
      label={
        isManualBlock
          ? t('tutorCalendar.statusManualBlock', 'Manual block')
          : t('tutorCalendar.statusExternal', 'Google Calendar')
      }
      sx={{
        flexShrink: 0,
        height: 36,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: '0.8125rem',
        bgcolor: alpha(accentColor, themeMode === 'dark' ? 0.28 : 0.18),
        color: themeMode === 'dark' ? '#FFF8E7' : '#5C3D00',
        border: '1px solid',
        borderColor: alpha(accentColor, 0.55),
        '& .MuiChip-label': { px: 1.25 },
      }}
    />
  ) : (
    <Chip
      color={statusChipColor(event?.status)}
      variant="filled"
      label={statusLabel(t, event?.status)}
      sx={{
        flexShrink: 0,
        height: 36,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: '0.8125rem',
        '& .MuiChip-label': { px: 1.25 },
      }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          margin: { xs: 1, sm: 2 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: '520px' },
          overflow: 'hidden',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
          background: themeMode === 'dark' ? '#1F2129' : '#FFFFFF',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.25, pr: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {isBlock
              ? t('tutorCalendar.blockDetails', 'Block details')
              : t('spaceCalendar.eventDetails', 'Event Details')}
          </Typography>
          {statusChip}
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, px: { xs: 2, sm: 2.5 }, pb: 2 }}>
        {event && schedule ? (
          <Stack spacing={2}>
            {isBlock ? (
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(accentColor, themeMode === 'dark' ? 0.24 : 0.16),
                    color: accentColor,
                  }}
                >
                  {isManualBlock ? (
                    <EventBusyRoundedIcon sx={{ fontSize: 26 }} />
                  ) : (
                    <SyncRoundedIcon sx={{ fontSize: 26 }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {displayTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.4 }}>
                    {isManualBlock
                      ? t(
                          'spaceCalendar.blockDetailsHint',
                          'This slot is blocked on the room calendar — it is not a room booking.',
                        )
                      : t(
                          'spaceCalendar.externalBlockHint',
                          'Imported from Google Calendar. It is not a Band Space booking.',
                        )}
                  </Typography>
                  {roomName ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {roomName}
                      </Typography>
                    </Stack>
                  ) : null}
                </Box>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <RoomBookingUserAvatar displayName={bookingHeaderName} size={48} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    fontWeight={700}
                    noWrap
                    sx={{ lineHeight: 1.3 }}
                  >
                    {bookingHeaderName}
                  </Typography>
                  {bookerPhone ? (
                    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ mt: 0.35 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="a"
                        href={`tel:${bookerPhone}`}
                        sx={{ textDecoration: 'none' }}
                        noWrap
                      >
                        {bookerPhone}
                      </Typography>
                    </Stack>
                  ) : null}
                  {bookerBand ? <UserBandAffiliationLine bandName={bookerBand} /> : null}
                  {!bookerName && !bookerPhone && !bookerBand && roomName ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {roomName}
                      </Typography>
                    </Stack>
                  ) : null}
                </Box>
              </Stack>
            )}

            <TutorBookingScheduleBlock schedule={schedule} />

            {!isBlock ? (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MeetingRoomRoundedIcon
                    sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                  />
                  <Stack spacing={0.35} minWidth={0} sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {roomName || t('spaceCalendar.room', 'Room')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('spaceCalendar.booking', 'Booking')}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={deletingBlock}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          {t('common.close', 'Close')}
        </Button>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {canDeleteBlock && isManualBlock && calendarEventId && onRequestDeleteBlock ? (
            <Button
              onClick={onRequestDeleteBlock}
              variant="outlined"
              color="error"
              disabled={deletingBlock}
              sx={{
                borderRadius: 2,
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {t('tutorCalendar.deleteBlock', 'Delete block')}
            </Button>
          ) : null}
          {bookingDetailsTo ? (
            <Button
              component={RouterLink}
              to={bookingDetailsTo}
              variant="contained"
              onClick={onClose}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: 2,
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {t('spaceCalendar.viewBookingDetails', 'View booking details')}
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
